<template>
  <div class="shop-categories">
    <div class="shop-categories__items">
      <Draggable
        :value="categories"
        @input="
          v => {
            onSort(v)
            $emit('track', 'CategoryReorder')
          }
        "
        class="shop-categories__content"
      >
        <Category
          v-for="(item, index) in categories"
          :active="selected === item"
          :key="index"
          :index="index"
          :value="item"
          @click="select(item)"
        />
      </Draggable>
      <Category v-if="canAdd" @click="add" placeholder />
      <Category
        v-if="shopExternalCategory"
        :active="selected === shopExternalCategory"
        :value="shopExternalCategory"
        :external="true"
        @click="select(shopExternalCategory)"
      />
    </div>
    <div v-if="selected && !selectedIsLocked" class="shop-categories__actions">
      <ButtonComponent
        :icon="['fal', 'drafting-compass']"
        :label="$tc('floorplan', 2) + ' (' + ($path('floorplans.length', categories) || 0) + ')'"
        @click="
          () => {
            showFloorplans = true
            $emit('track', 'CategoryFloorplan')
          }
        "
      >
        <template #label>
          <span>{{ $tc('floorplan', 2) }}</span>
          <Badge :value="selected.floorplans.length" class="ml-1" />
        </template>
      </ButtonComponent>
      <ConditionsInput
        :value="selected.conditions"
        style="align-self: stretch"
        @click="
          () => {
            showConditions = true
            $emit('track', 'CategoryConditions')
          }
        "
      />
      <Intro
        :description.sync="selected.description"
        :label="$t('description')"
        @track="() => $emit('track', 'CategoryIntro')"
      />
      <DropdownComponent>
        <ButtonComponent :icon="['fal', 'ellipsis-v']" />
        <template #dropdown>
          <ButtonComponent
            v-if="!isExternal"
            :icon="['fal', 'copy']"
            :label="$t('clone')"
            @click="
              () => {
                clone(selected)
                $emit('track', 'CategoryDuplicate')
              }
            "
          />
          <ButtonComponent :icon="['fal', 'times']" :label="$t('remove')" @click="remove(selected)" />
        </template>
      </DropdownComponent>
      <ConditionsModal v-if="showConditions" v-model="selected.conditions" @close="showConditions = false" />
      <Portal>
        <FloorplanDialog v-if="showFloorplans" v-model="selected.floorplans" @close="showFloorplans = false" />
      </Portal>
    </div>
  </div>
</template>

<script>
import Model from '@/models/Shop/category'
import ObjectId from 'mongo-objectid'
import { mapState } from 'vuex'

import Draggable from 'vuedraggable'
import Category from './Category'
import Intro from '../Intro'
import Badge from '@/components/Badge'
import ConditionsInput from '@/components/Shop/Conditions/Input'
import ConditionsModal from '@/components/Shop/Conditions/Modal'
import FloorplanDialog from '@/components/Shop/Floorplans/Dialog'
import Portal from '@/components/Portal'
import { AlertComponent as Alert } from 'vue-elder-alert'
import { DropdownComponent } from 'vue-elder-dropdown'

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    selected: Object,
  },
  watch: {
    value: {
      handler(val) {
        if (!val.length) this.add()
        if (!this.selected) this.select(val[0])
      },
      immediate: true,
    },
  },
  data() {
    return {
      showConditions: false,
      showFloorplans: false,
    }
  },
  computed: {
    ...mapState('Shop', ['item']),
    selectedIsLocked() {
      if (!this.item.id) return false

      let selectedCategory = this.item.categories.find(c => c.id === this.selected.id)
      if (!selectedCategory) return
      return ['signed', 'accepted'].includes(selectedCategory.status)
    },
    isExternal() {
      return this.$path('tags', this.selected) && this.selected.tags.includes('kvass:shop-external-products')
    },
    categories() {
      return this.value.filter(category => !category.tags.includes('kvass:shop-external-products'))
    },
    shopExternalCategory() {
      return this.value.find(category => category.tags.includes('kvass:shop-external-products'))
    },
    canAdd() {
      return !['signed', 'accepted'].includes(this.$path('item.contract.status'))
    },
  },
  methods: {
    add() {
      let item = Model()
      this.$emit('input', [...this.value, item])

      this.select(item)
      this.$nextTick(() => {
        Array.from(this.$el.querySelectorAll('input.shop-category__label:not(.shop-category__label-external)'))
          .pop()
          .focus()
      })
    },
    clone(item) {
      let index = this.value.indexOf(item)
      let clone = structuredClone(item)

      let productMap = Object.fromEntries(
        clone.categories
          .map(subcategory => subcategory.items)
          .flat(Infinity)
          .map(i => [i.id, new ObjectId().hex]),
      )

      const MapConditions = conditions => {
        return {
          if: conditions.if.map(e => productMap[e] || e),
          ifnot: conditions.ifnot.map(e => productMap[e] || e),
        }
      }

      clone.id = new ObjectId().hex
      clone.conditions = MapConditions(clone.conditions)
      clone.floorplans = clone.floorplans.map(floorplan => {
        floorplan.conditions = MapConditions(floorplan.conditions)
        return floorplan
      })
      clone.categories = clone.categories.map(subcategory => {
        subcategory.id = new ObjectId().hex
        subcategory.conditions = MapConditions(subcategory.conditions)
        subcategory.items = subcategory.items.map(item => {
          item.id = productMap[item.id]
          item.conditions = MapConditions(item.conditions)
          return item
        })

        return subcategory
      })

      this.value.splice(index + 1, 0, clone)
      this.$emit('input', this.value)
    },
    remove(item) {
      this.$emit(
        'input',
        this.value.filter(v => v !== item),
      )
    },
    select(item) {
      this.$emit('update:selected', item)
    },
    onSort(items) {
      this.$emit('input', [...items, this.shopExternalCategory].filter(Boolean))
    },
  },
  components: {
    Category,
    Draggable,
    ConditionsInput,
    ConditionsModal,
    FloorplanDialog,
    Portal,
    Badge,
    Intro,
    Alert,
    DropdownComponent,
  },
}
</script>

<style lang="scss">
.shop-categories {
  &__items {
    display: flex;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    padding: 2rem;
    background-color: css-lighten('border-color', 9%);
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
  }

  .shop-category--active {
    background-color: css-lighten('border-color', 9%);
  }

  &__content {
    display: flex;
    overflow-x: scroll;
    counter-reset: shop-categories;
    scroll-snap-type: x mandatory;
  }
}
</style>
